import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, Shield, CheckCircle, ChevronDown, ChevronUp, Calendar, ArrowRight, Heart, Activity, Stethoscope, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import LandingPageHeader from '../components/LandingPageHeader';
import { SEOHead, BreadcrumbSchema, FAQSchema, MedicalSpecialtySchema } from '../components/SEOHead';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CardiologyPage = () => {
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', treatment: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.treatment) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/specialty-leads`, { ...formData, specialty: 'Cardiology', source_page: '/cardiology' });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'cardiology_lead', form_department: 'Cardiology', form_source: '/cardiology' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/cardiology_lead' }); }
      toast.success('Thank you! Our cardiology team will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', treatment: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const treatments = [
    'Heart Check-Up', 'ECG / ECHO', 'Chest Pain Evaluation', 'Angiogram / Angioplasty',
    'Pacemaker', 'Heart Failure Treatment', 'Arrhythmia Care', 'Other',
  ];

  const services = [
    { title: 'Heart Attack Treatment', desc: '24/7 emergency cardiac care with rapid response, thrombolysis, and life-saving interventions', icon: Heart, color: '#c41e3a' },
    { title: 'ECG, ECHO & TMT', desc: 'Advanced non-invasive cardiac diagnostics including electrocardiography, echocardiography & treadmill testing', icon: Activity, color: '#0066b3' },
    { title: 'Interventional Cardiology', desc: 'Coronary angiography, angioplasty, stenting, and catheter-based procedures for heart blockages', icon: Stethoscope, color: '#7b1fa2' },
    { title: 'Pacemaker Implantation', desc: 'Permanent and temporary pacemaker placement for heart rhythm disorders and bradycardia', icon: Shield, color: '#00897b' },
    { title: 'Heart Failure Management', desc: 'Comprehensive treatment plans for congestive heart failure with medication and lifestyle management', icon: Heart, color: '#f57c00' },
    { title: 'Preventive Cardiology', desc: 'Risk assessment, cholesterol management, hypertension control & heart-healthy lifestyle guidance', icon: CheckCircle, color: '#0097a7' },
  ];

  const stats = [
    { value: '10,000+', label: 'Cardiac Patients Treated' },
    { value: '24/7', label: 'Emergency Cardiac Care' },
    { value: 'NABH', label: 'Accredited Facility' },
    { value: '40+', label: 'Years of Excellence' },
  ];

  const trustPoints = [
    '24/7 Emergency Cardiac Care', 'Advanced Cath Lab Facility', 'ICU with Intensivist',
    'Experienced Cardiologists', 'Insurance & TPA Cashless', 'NABH Accredited Hospital',
  ];

  const testimonials = [
    { name: 'Rajesh M.', location: 'Triplicane', rating: 5, text: 'Had a heart attack and was rushed to Sakthi Hospital at midnight. The emergency team saved my life. The cardiac care here is world-class. Forever grateful to the doctors.' },
    { name: 'Venkatesh P.', location: 'Royapettah', rating: 5, text: 'Regular heart check-ups at Sakthi Hospital. The cardiology department is excellent with modern equipment. The doctors explain everything clearly and are very caring.' },
    { name: 'Lakshmi S.', location: 'Mylapore', rating: 5, text: 'My father had pacemaker surgery at Sakthi Hospital. The procedure went smoothly and the post-operative care was outstanding. Highly recommend for heart-related issues.' },
  ];

  const faqs = [
    { q: 'What are the warning signs of a heart attack?', a: 'Common signs include chest pain or discomfort, shortness of breath, pain in the arm/jaw/back, cold sweats, and nausea. If you experience any of these, call our emergency number immediately: +91 44 2844 2626.' },
    { q: 'How often should I get a cardiac check-up?', a: 'Adults over 35 should get annual cardiac screening. Those with risk factors (diabetes, hypertension, family history, smoking) should start earlier. Our preventive cardiology program includes comprehensive heart health assessment.' },
    { q: 'Do you offer emergency cardiac care 24/7?', a: 'Yes, Sakthi Hospital provides round-the-clock emergency cardiac care with a dedicated team of cardiologists, ICU with intensivist support, and advanced cardiac monitoring equipment.' },
    { q: 'What cardiac diagnostic tests do you offer?', a: 'We offer ECG (Electrocardiogram), 2D Echocardiography, TMT (Treadmill Test), Holter monitoring, cardiac stress testing, and advanced coronary angiography in our state-of-the-art cath lab.' },
    { q: 'Is angioplasty available at Sakthi Hospital?', a: 'Yes, we have a modern cath lab for coronary angiography and angioplasty procedures. Our interventional cardiologists are highly experienced in stenting and other catheter-based cardiac interventions.' },
  ];

  const LeadForm = ({ className = '' }) => (
    <form onSubmit={handleSubmit} className={className}>
      <input type="hidden" id="zc_gad" name="zc_gad" value="" />
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium text-gray-700">Your Name</Label>
          <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Full Name" className="mt-1 h-11" data-testid="cardio-form-name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
          <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98402 64444" className="mt-1 h-11" data-testid="cardio-form-phone" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Email Address</Label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="mt-1 h-11" data-testid="cardio-form-email" />
        </div>
        <div>
          <Label className="text-sm font-medium text-gray-700">Select Concern</Label>
          <Select value={formData.treatment} onValueChange={(v) => setFormData({...formData, treatment: v})}>
            <SelectTrigger className="mt-1 h-11" data-testid="cardio-form-treatment"><SelectValue placeholder="Select Concern" /></SelectTrigger>
            <SelectContent>{treatments.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#c41e3a] hover:bg-[#a0182f] text-white font-semibold rounded-lg" data-testid="cardio-form-submit">
          {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> Submitting...</> : <><Calendar size={18} className="mr-2" /> Book Heart Check-Up</>}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="cardiology-page">
      <SEOHead
        title="Best Cardiology Hospital in Chennai - Heart Treatment | Sakthi Hospital Triplicane"
        description="24/7 emergency cardiac care at Sakthi Hospital Chennai. Heart attack treatment, ECG, ECHO, angioplasty, pacemaker implantation. Experienced cardiologists. NABH Accredited. Book heart check-up today."
        path="/cardiology"
        keywords="cardiology hospital Chennai, heart attack treatment Triplicane, best cardiologist Chennai, ECG ECHO test, angioplasty Chennai, pacemaker surgery, cardiac care hospital near me, emergency heart care Chennai, chest pain treatment"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Cardiology', path: '/cardiology' }]} />
      <FAQSchema questions={faqs} />
      <MedicalSpecialtySchema
        name="Cardiology"
        description="Complete cardiac care at Sakthi Hospital Chennai. Emergency heart attack treatment, ECG, ECHO, TMT, angioplasty, pacemaker implantation, and preventive cardiology."
        procedures={['Heart Attack Treatment', 'ECG', '2D Echocardiography', 'TMT', 'Coronary Angiography', 'Angioplasty & Stenting', 'Pacemaker Implantation', 'Heart Failure Management']}
      />
      <LandingPageHeader accentColor="#c41e3a" subtitle="Cardiology & Heart Care" />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#fff5f5] via-white to-[#fce4e8]" data-testid="cardio-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#c41e3a]/5 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#c41e3a]/10 text-[#c41e3a] rounded-full text-xs font-semibold">24/7 Emergency Care</span>
                <span className="px-3 py-1 bg-[#c41e3a]/10 text-[#c41e3a] rounded-full text-xs font-semibold">NABH Accredited</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] leading-[1.1] tracking-tight" data-testid="cardio-headline">
                Expert Heart Care{' '}
                <span className="text-[#c41e3a]">You Can Trust</span>
                <span className="block text-lg sm:text-xl font-semibold text-[#64748b] mt-3">Advanced Cardiology at Sakthi Hospital, Chennai</span>
              </h1>
              <p className="text-base text-[#5a6b7c] leading-relaxed max-w-lg">
                From routine heart check-ups to emergency cardiac interventions — our experienced cardiologists provide comprehensive heart care with modern diagnostics and 24/7 emergency support.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-[#c41e3a] text-white px-7 py-3.5 rounded-lg font-semibold shadow-lg shadow-[#c41e3a]/25 hover:bg-[#a0182f] transition-all flex items-center gap-2" data-testid="cardio-call-btn">
                  <Phone size={18} /> Emergency: Call Now
                </a>
                <a href="#cardio-booking" className="bg-white text-[#c41e3a] border-2 border-[#c41e3a] px-7 py-3.5 rounded-lg font-semibold hover:bg-[#c41e3a] hover:text-white transition-all flex items-center gap-2">
                  <Calendar size={18} /> Book Heart Check-Up
                </a>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-red-100">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold text-[#0f172a]">Book a Heart Check-Up</h3>
                <p className="text-sm text-[#64748b]">Free consultation & cardiac assessment</p>
              </div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#c41e3a] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {stats.map((s) => (<div key={s.value}><div className="text-3xl font-extrabold">{s.value}</div><div className="text-sm opacity-80 mt-1">{s.label}</div></div>))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Complete Cardiac Care Services</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">Advanced diagnostics, emergency treatment, and preventive cardiology under one roof</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="bg-gradient-to-br from-[#fff5f5] to-white rounded-2xl p-6 border border-red-100 hover:shadow-lg transition-all" data-testid={`cardio-service-${s.title}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.color}15`, color: s.color }}><s.icon size={28} /></div>
                <h3 className="text-lg font-bold text-[#0f172a] mb-2">{s.title}</h3>
                <p className="text-sm text-[#5a6b7c] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Points */}
      <section className="py-16 bg-gradient-to-b from-[#fff5f5] to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-3">Why Choose Our Cardiology Department</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustPoints.map((point) => (
              <div key={point} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-red-50">
                <CheckCircle size={22} className="text-[#c41e3a] flex-shrink-0" /><span className="font-medium text-[#0f172a]">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Patient Testimonials</h2></div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-gradient-to-br from-[#fff5f5] to-white rounded-2xl p-6 shadow-md border border-red-50" data-testid={`cardio-testimonial-${t.name}`}>
                <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={`star-${j}`} size={16} fill="#d4a24c" stroke="#d4a24c" />)}</div>
                <p className="text-sm text-[#3a4a5c] leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 bg-[#c41e3a] rounded-full flex items-center justify-center text-white font-bold text-sm">{t.name[0]}</div>
                  <div><div className="font-semibold text-sm text-[#0f172a]">{t.name}</div><div className="text-xs text-[#94a3b8]">{t.location}, Chennai</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-b from-[#fff5f5] to-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-10">FAQs - Heart & Cardiology Care</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={faq.q} className="border border-red-100 rounded-xl overflow-hidden" data-testid={`cardio-faq-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#fff5f5] transition-colors">
                  <span className="font-semibold text-[#0f172a] pr-4">{faq.q}</span>
                  {openFaq === idx ? <ChevronUp size={20} className="text-[#c41e3a] flex-shrink-0" /> : <ChevronDown size={20} className="text-[#94a3b8] flex-shrink-0" />}
                </button>
                {openFaq === idx && <div className="px-5 pb-5 text-sm text-[#5a6b7c] leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0f172a] mb-8">Visit Our Hospital</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden h-72 shadow-lg">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.0!2d80.2757397!3d13.0570045!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526605d0a030d5%3A0x6e079bd99529d311!2sSakthi%20Hospital!5e0!3m2!1sen!2sin!4v1704067200000!5m2!1sen!2sin" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Sakthi Hospital Location"></iframe>
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-start gap-3"><MapPin size={20} className="text-[#c41e3a] mt-1 flex-shrink-0" /><div><p className="font-semibold text-[#0f172a]">Sakthi Hospital & Research Centre</p><p className="text-sm text-[#5a6b7c]">175, Big Street, Triplicane, Chennai - 600005</p></div></div>
              <div className="flex items-center gap-3"><Phone size={20} className="text-[#c41e3a] flex-shrink-0" /><p className="text-sm text-[#5a6b7c]">+91 44 2844 2626 | +91 98402 64444</p></div>
              <div className="flex items-center gap-3"><Clock size={20} className="text-[#c41e3a] flex-shrink-0" /><p className="text-sm text-[#5a6b7c]">OPD: 9:00 AM - 9:00 PM | Emergency: 24/7</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cardio-booking" className="py-16 bg-[#c41e3a]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-white space-y-4">
              <h2 className="text-3xl font-bold">Don't Ignore Heart Symptoms. Get Checked Today.</h2>
              <p className="opacity-90">Book your cardiac consultation now. Early detection saves lives.</p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919840264444" className="bg-white text-[#c41e3a] px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors"><Phone size={18} /> Call Now</a>
                <a href="https://wa.me/919840264444" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:bg-[#20bd5a] transition-colors">WhatsApp Now</a>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-bold text-center text-[#0f172a] mb-4">Book Heart Check-Up</h3>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <div className="bg-[#0f172a] text-white py-6 text-center">
        <Link to="/" className="text-sm text-white/70 hover:text-[#c41e3a] transition-colors flex items-center gap-2 justify-center"><ArrowRight size={14} className="rotate-180" /> Back to Sakthi Hospital Home</Link>
        <p className="text-xs text-white/50 mt-2">© 2025 Sakthi Hospital & Research Centre. All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default CardiologyPage;
